import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';

import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Glyphicon, Button, OverlayTrigger, Tooltip } from '@bingads-webui-react/primitive';
import { withOverflowProp } from '@bingads-webui-react/with-overflow-prop';

import { getTreeNodeStyles } from '../styles';
import { IconName, Severity } from '../constants';

const OverlayTriggerContainer = ({ renderOverlayTrigger, children, ...rest }) => (
  renderOverlayTrigger
    ? <OverlayTrigger {...rest}>{children}</OverlayTrigger>
    : <>{children}</>
);

OverlayTriggerContainer.propTypes = {
  renderOverlayTrigger: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

OverlayTriggerContainer.defaultProps = {
  renderOverlayTrigger: true,
};

const TreeNodeWithTooltipBase = ({
  // hoc props
  forwardedRef,
  isTextOverflow,
  // node props
  id,
  name,
  severity,
  iconTooltipText,
  isSelected,
  onClick,
}) => {
  const classes = useDefaultStyles(getTreeNodeStyles, { isSelected });
  const hasIconTooltipText = !!iconTooltipText;
  const overflowOverlayProps = {
    placement: 'top',
    overlay: (
      <Tooltip
        id="budget-center-sidebar-tree-node-overflow-tooltip-top"
        className={classnames({ [classes.HiddenTooltip]: !isTextOverflow })}
      >
        {name}
      </Tooltip>
    ),
  };
  const iconOverlayProps = {
    placement: 'top',
    overlay: (
      <Tooltip id="budget-center-sidebar-tree-node-icon-tooltip">
        {iconTooltipText}
      </Tooltip>
    ),
  };

  return (
    <OverlayTriggerContainer
      {...overflowOverlayProps}
      renderOverlayTrigger={!hasIconTooltipText}
    >
      <Button
        key={id}
        btnStyle="command"
        className={classnames(
          'budget-center-sidebar-tree-node',
          `budget-center-sidebar-tree-node-${id}`,
          { 'budget-center-sidebar-tree-node-selected': isSelected },
          classes.TreeNode
        )}
        aria-label={name}
        onClick={onClick}
      >
        <div className={classnames('budget-center-sidebar-tree-node-leftPadding', classes.TreeNodeLeftPadding)} />
        <OverlayTriggerContainer
          {...iconOverlayProps}
          renderOverlayTrigger={hasIconTooltipText}
        >
          <div className={classnames('budget-center-sidebar-tree-node-icon', classes.TreeNodeIcon)}>
            {
              IconName[severity] &&
              <Glyphicon
                className={classnames(
                  'budget-center-sidebar-tree-node-glyphicon',
                  classes.TreeNodeGlyphicon,
                  classes[`TreeNodeGlyphicon-${severity}`]
                )}
                glyph={`ba-${IconName[severity]}`}
              />
            }
          </div>
        </OverlayTriggerContainer>
        <OverlayTriggerContainer
          {...overflowOverlayProps}
          renderOverlayTrigger={hasIconTooltipText}
        >
          <div ref={forwardedRef} className={classnames('budget-center-sidebar-tree-node-name', classes.TreeNodeName)}>
            {name}
          </div>
        </OverlayTriggerContainer>
      </Button>
    </OverlayTriggerContainer>
  );
};

TreeNodeWithTooltipBase.propTypes = {
  forwardedRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  isTextOverflow: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  severity: PropTypes.oneOf(_.values(Severity)),
  iconTooltipText: PropTypes.string,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

TreeNodeWithTooltipBase.defaultProps = {
  forwardedRef: null,
  isTextOverflow: false,
  severity: undefined,
  iconTooltipText: undefined,
  isSelected: false,
};

export const TreeNodeWithTooltip = withOverflowProp(TreeNodeWithTooltipBase);
